import { A } from "@solidjs/router";
import type { EvalComparisonDetailResponse } from "contracts/api/evals/comparisons";
import { EVAL_VARIANT_SLOT } from "contracts/enums/evals";
import { Show } from "solid-js";
import {
  EVAL_JUDGE_KIND_COPY,
  EVAL_VARIANT_SLOT_COPY,
  EVIDENCE_STATE_COPY,
  EVIDENCE_STATE_TONE,
} from "../../enums/evals";
import {
  evidenceState,
  humanVerdictState,
  standingVerdict,
} from "./comparisonState";
import { EvalJudgmentPanel } from "./EvalJudgmentPanel";
import { EvalVariantOutput } from "./EvalVariantOutput";
import { createEvalComparison, invalidateEvalComparison } from "./evalQueries";
import { HumanVerdictForm } from "./HumanVerdictForm";

/**
 * One comparison, side by side. The backend refuses this read with a 409 until
 * both runs have a completed attempt; the query layer turns that into
 * `ready: false` and this surface renders it as the progress state it is, then
 * keeps polling. It is never an error toast — nothing has gone wrong.
 */
export function EvalComparisonDetail(props: { comparisonId: string }) {
  const comparison = createEvalComparison(() => props.comparisonId);
  const detail = () => {
    const read = comparison.data;
    return read?.ready ? read.detail : undefined;
  };
  const stillRunning = () => comparison.data?.ready === false;

  return (
    <main class="landing">
      <div class="landing-bar">
        <A href="/evals" class="landing-brand">
          ← Evals
        </A>
        <span class="landing-tag">Comparison</span>
      </div>
      <div class="landing-main">
        <Show
          when={detail()}
          fallback={
            <Show
              when={stillRunning()}
              fallback={<div class="loading">Loading comparison…</div>}
            >
              <div class="empty">
                <div class="empty-title">Still running</div>
                <div class="empty-hint">
                  Both variants have to finish before their outputs can be
                  compared. This page updates itself; the batch shows how far
                  along the pair is.
                </div>
              </div>
            </Show>
          }
        >
          {(ready) => (
            <ComparisonBody
              detail={ready()}
              onChanged={() =>
                void invalidateEvalComparison(props.comparisonId)
              }
            />
          )}
        </Show>
      </div>
    </main>
  );
}

function ComparisonBody(props: {
  detail: EvalComparisonDetailResponse;
  onChanged: () => void;
}) {
  const labels = () => ({
    a: props.detail.variants.a.label,
    b: props.detail.variants.b.label,
  });
  const evidence = () => evidenceState(props.detail.evidenceMatches);

  return (
    <>
      <div class="landing-head-row">
        <div>
          <h1 class="landing-h1 mono">{props.detail.url}</h1>
          <p class="landing-sub pg-lede">
            <span
              class={`badge badge-status ${EVIDENCE_STATE_TONE[evidence()]}`}
            >
              {EVIDENCE_STATE_COPY[evidence()]}
            </span>
          </p>
        </div>
        <A href={`/evals/batches/${props.detail.batchId}`} class="btn">
          Batch
        </A>
      </div>

      <EvalJudgmentPanel judgments={props.detail.judgments} labels={labels()} />

      <HumanVerdictForm
        comparisonId={props.detail.comparisonId}
        state={humanVerdictState(props.detail)}
        labels={labels()}
        onRecorded={props.onChanged}
      />

      <Show when={standingVerdict(props.detail.judgments)}>
        {(verdict) => (
          <p class="landing-sub">
            Standing:{" "}
            {EVAL_JUDGE_KIND_COPY[verdict().standing.judgeKind].toLowerCase()}.
          </p>
        )}
      </Show>

      <div class="pg-cards">
        <EvalVariantOutput
          side={EVAL_VARIANT_SLOT_COPY[EVAL_VARIANT_SLOT.A]}
          briefing={props.detail.variants.a}
        />
        <EvalVariantOutput
          side={EVAL_VARIANT_SLOT_COPY[EVAL_VARIANT_SLOT.B]}
          briefing={props.detail.variants.b}
        />
      </div>
    </>
  );
}
